import { useMemo, useState, useCallback } from "react";
import { format, addDays, addMinutes, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, ChevronLeft, Check, Square } from "lucide-react";
import SportIcon from "@/components/SportIcon";
import { type PricingRule, type SpecialPriceDate, calculateBookingPrice } from "@/lib/pricing";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AutoBookingView from "./AutoBookingView";
import CategoryBookingView from "./CategoryBookingView";
import WaitlistCta from "./WaitlistCta";

interface CourtInfo {
  id: string;
  name: string;
  court_type?: string;
  surface_type?: string | null;
  max_players?: number;
}

interface BookingInfo {
  id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
}

interface BookingQuickViewProps {
  courts: CourtInfo[];
  bookings: BookingInfo[];
  hallHours: { start: number; end: number };
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  bookingHorizon: number;
  timeStepMinutes: number;
  onSelectSlot: (courtId: string, startTime: Date) => void;
  pricingRules?: PricingRule[];
  specialDates?: SpecialPriceDate[];
  hallId?: string;
  defaultDuration?: number;
  maxDuration?: number;
  categorySlug?: string;
  onBookMultiple?: (courtIds: string[], startTime: Date, duration: number) => void;
  courtAssignmentMode?: "manual" | "auto" | "by_category";
}

interface TimeSlot {
  time: string;
  availableCourts: number;
  totalCourts: number;
  perHour: number | null;
  isPeak: boolean;
}

const DURATION_OPTIONS = [30, 60, 90, 120];

// Wrapper: pure dispatcher. Each branch renders a self-contained child
// component that owns its own hooks, so Rules of Hooks is never violated
// by the early-return branches.
export default function BookingQuickView(props: BookingQuickViewProps) {
  const { courtAssignmentMode = "manual", onBookMultiple, ...rest } = props;
  const onBook = (courtIds: string[], startTime: Date, duration: number) => {
    if (onBookMultiple) onBookMultiple(courtIds, startTime, duration);
  };
  if (courtAssignmentMode === "auto") {
    return <AutoBookingView {...rest} onBook={onBook} />;
  }
  if (courtAssignmentMode === "by_category") {
    return <CategoryBookingView {...rest} onBook={onBook} />;
  }
  return <ManualBookingView {...props} />;
}

type ManualBookingViewProps = BookingQuickViewProps;

function ManualBookingView({
  courts,
  bookings,
  hallHours,
  selectedDate,
  onDateChange,
  bookingHorizon,
  timeStepMinutes,
  onSelectSlot,
  pricingRules,
  specialDates,
  hallId,
  defaultDuration = 60,
  maxDuration = 120,
  categorySlug,
  onBookMultiple,
}: ManualBookingViewProps) {
  const { t } = useTranslation('booking');
  const navigate = useNavigate();

  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedCourts, setSelectedCourts] = useState<string[]>([]);
  const [duration, setDuration] = useState(defaultDuration);

  // Available duration options (capped by maxDuration)
  const durationOptions = useMemo(
    () => DURATION_OPTIONS.filter((d) => d >= (defaultDuration < 60 ? defaultDuration : 30) && d <= maxDuration),
    [defaultDuration, maxDuration],
  );

  // Generate dates
  const dates = useMemo(() => {
    return Array.from({ length: Math.min(bookingHorizon, 7) }, (_, i) =>
      startOfDay(addDays(new Date(), i))
    );
  }, [bookingHorizon]);
  const isQuickDate = dates.some((d) => d.getTime() === selectedDate.getTime());

  // All time slots (including fully booked ones)
  const allSlots = useMemo(() => {
    const slots: TimeSlot[] = [];
    const now = new Date();
    const cat = categorySlug ?? "standard";

    for (let min = hallHours.start * 60; min < hallHours.end * 60; min += timeStepMinutes) {
      const h = Math.floor(min / 60);
      const m = min % 60;
      const timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

      const slotStart = new Date(selectedDate);
      slotStart.setHours(h, m, 0, 0);

      if (slotStart < now) continue;

      const freeCourts = courts.filter((court) => {
        return !bookings.some(
          (b) =>
            b.court_id === court.id &&
            b.status !== "cancelled" &&
            new Date(b.start_time) <= slotStart &&
            new Date(b.end_time) > slotStart
        );
      });

      let perHour: number | null = null;
      let isPeak = false;
      if (pricingRules && hallId) {
        const price = calculateBookingPrice(pricingRules, hallId, slotStart, duration, cat, specialDates ?? []);
        perHour = price.perHour;
        isPeak = price.isPeak;
      }

      slots.push({
        time: timeStr,
        availableCourts: freeCourts.length,
        totalCourts: courts.length,
        perHour,
        isPeak,
      });
    }
    return slots;
  }, [courts, bookings, hallHours, selectedDate, timeStepMinutes, pricingRules, hallId, duration, specialDates, categorySlug]);

  // 009/T2: finns det över huvud taget ett framtida tidsfönster kvar på det valda
  // datumet? allSlots tömmer sig bara när ALLA luckor passerat (fulla tider
  // pushas också), så tomläget inträffar i praktiken sent på dagen. Utan ett
  // framtida fönster finns ingen tid att köa på, och då ska CTA:n inte visas —
  // en köpost på en passerad tid kan aldrig erbjudas.
  const remainingWindow = useMemo(() => {
    const now = new Date();
    for (let min = hallHours.start * 60; min < hallHours.end * 60; min += timeStepMinutes) {
      const slotStart = new Date(selectedDate);
      slotStart.setHours(Math.floor(min / 60), min % 60, 0, 0);
      if (slotStart > now) {
        return {
          startTime: `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`,
          durationMinutes: duration,
        };
      }
    }
    return null;
  }, [hallHours, selectedDate, timeStepMinutes, duration]);

  // Group slots by time of day
  const timeGroups = useMemo(() => {
    const groups: { label: string; labelKey: string; slots: TimeSlot[] }[] = [
      { label: "Förmiddag", labelKey: "morning", slots: [] },
      { label: "Eftermiddag", labelKey: "afternoon", slots: [] },
      { label: "Kväll", labelKey: "evening", slots: [] },
    ];

    allSlots.forEach((slot) => {
      const hour = parseInt(slot.time.split(":")[0]);
      if (hour < 12) groups[0].slots.push(slot);
      else if (hour < 17) groups[1].slots.push(slot);
      else groups[2].slots.push(slot);
    });

    return groups.filter((g) => g.slots.length > 0);
  }, [allSlots]);

  // Court availability check for a given duration
  const isCourtFreeForDuration = useCallback(
    (courtId: string, time: string, dur: number) => {
      const [h, m] = time.split(":").map(Number);
      const slotStart = new Date(selectedDate);
      slotStart.setHours(h, m, 0, 0);
      const slotEnd = addMinutes(slotStart, dur);

      return !bookings.some(
        (b) =>
          b.court_id === courtId &&
          b.status !== "cancelled" &&
          new Date(b.start_time) < slotEnd &&
          new Date(b.end_time) > slotStart
      );
    },
    [bookings, selectedDate],
  );

  // Price for a specific court at the selected time + duration
  const getCourtPrice = useCallback(
    (_courtId: string, time: string, dur: number): number => {
      if (!pricingRules || !hallId) return 0;
      const [h, m] = time.split(":").map(Number);
      const slotStart = new Date(selectedDate);
      slotStart.setHours(h, m, 0, 0);
      const cat = categorySlug ?? "standard";
      const price = calculateBookingPrice(pricingRules, hallId, slotStart, dur, cat, specialDates ?? []);
      return price.total;
    },
    [pricingRules, hallId, selectedDate, specialDates, categorySlug],
  );

  const toggleCourt = (courtId: string) => {
    setSelectedCourts((prev) =>
      prev.includes(courtId) ? prev.filter((id) => id !== courtId) : [...prev, courtId],
    );
  };

  const handleDurationChange = (newDuration: number) => {
    setDuration(newDuration);
    // Clear selected courts that are no longer available for the new duration
    if (selectedTime) {
      setSelectedCourts((prev) =>
        prev.filter((cId) => isCourtFreeForDuration(cId, selectedTime, newDuration)),
      );
    }
  };

  // Calculate totals
  const totalPrice = useMemo(() => {
    if (!selectedTime) return 0;
    return selectedCourts.reduce((sum, cId) => sum + getCourtPrice(cId, selectedTime, duration), 0);
  }, [selectedCourts, selectedTime, duration, getCourtPrice]);

  const handleBookSelected = () => {
    if (!selectedTime || selectedCourts.length === 0) return;
    const [h, m] = selectedTime.split(":").map(Number);
    const startTime = new Date(selectedDate);
    startTime.setHours(h, m, 0, 0);

    if (onBookMultiple) {
      onBookMultiple(selectedCourts, startTime, duration);
    } else {
      // Fallback: book first court
      onSelectSlot(selectedCourts[0], startTime);
    }
  };

  const handleCreateActivity = () => {
    if (!selectedTime || selectedCourts.length === 0) return;
    const [h, m] = selectedTime.split(":").map(Number);
    const startDT = new Date(selectedDate);
    startDT.setHours(h, m, 0, 0);
    const endDT = new Date(startDT.getTime() + duration * 60000);

    navigate("/create-match", {
      state: {
        courtIds: selectedCourts,
        startTime: startDT.toISOString(),
        endTime: endDT.toISOString(),
      },
    });
  };

  return (
    <div className="space-y-5">
      {/* Date selector - round buttons */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {format(dates[0], "MMMM yyyy", { locale: sv })}
        </p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {dates.map((date) => {
            const isSelected = selectedDate.toDateString() === date.toDateString();
            const isToday = date.toDateString() === new Date().toDateString();
            return (
              <button
                key={date.toISOString()}
                className={cn(
                  "flex flex-col items-center justify-center h-14 w-14 rounded-full text-sm font-medium transition-colors shrink-0",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : isToday
                    ? "ring-2 ring-primary/30 hover:bg-muted"
                    : "hover:bg-muted"
                )}
                onClick={() => {
                  onDateChange(date);
                  setSelectedTime(null);
                  setSelectedCourts([]);
                }}
              >
                <span className="text-[10px] uppercase leading-none">
                  {format(date, "EEE", { locale: sv }).slice(0, 2)}
                </span>
                <span className="font-semibold text-base leading-tight">
                  {format(date, "d")}
                </span>
              </button>
            );
          })}
          {bookingHorizon > 7 && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    "flex flex-col items-center justify-center h-14 w-14 rounded-full text-sm font-medium transition-colors shrink-0",
                    !isQuickDate ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  )}
                >
                  <CalendarIcon className="h-4 w-4" />
                  <span className="text-[10px] leading-none mt-0.5">
                    {!isQuickDate ? format(selectedDate, "d/M") : t('more')}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      onDateChange(startOfDay(date));
                      setSelectedTime(null);
                      setSelectedCourts([]);
                    }
                  }}
                  disabled={(date) =>
                    date < startOfDay(new Date()) ||
                    date > addDays(new Date(), bookingHorizon)
                  }
                  locale={sv}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* STEP 1: Time selection (shown when no time selected) */}
      {!selectedTime && (
        <div className="animate-in fade-in-0 duration-200">
          {allSlots.length === 0 ? (
            <div className="py-4 text-center space-y-2">
              <p className="text-sm text-muted-foreground">{t('noAvailableTimes')}</p>
              {/* 009/T2: tomläget inträffar i praktiken bara sent på dagen —
                  allSlots pushar även fulla tider, så listan tömmer sig först när
                  alla luckor passerat. Då finns ingen tid att köa på, och CTA:n
                  hålls tillbaka i stället för att skapa en meningslös köpost. */}
              {remainingWindow && (
                <>
                  <p className="text-sm text-muted-foreground">{t('noAvailableTimesHint')}</p>
                  <WaitlistCta
                    courts={courts}
                    date={selectedDate}
                    startTime={remainingWindow.startTime}
                    durationMinutes={remainingWindow.durationMinutes}
                  />
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {timeGroups.map((group) => {
                const hasPeak = group.slots.some((s) => s.isPeak);
                return (
                  <div key={group.labelKey} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t(group.labelKey)}
                      </p>
                      {hasPeak && (
                        <span className="text-xs text-amber-600 font-medium">Peak</span>
                      )}
                    </div>
                    <div className="rounded-xl border divide-y">
                      {group.slots.map((slot) => {
                        const isFull = slot.availableCourts === 0;
                        return (
                          // 009/T1: en full lucka var tidigare en död disabled-rad
                          // utan utväg. Raden ligger nu i en wrapper så kö-CTA:n
                          // kan stå bredvid — en knapp kan inte ligga i en knapp.
                          <div key={slot.time} className="flex items-center">
                          <button
                            className={cn(
                              "w-full flex items-center justify-between px-4 py-3 text-sm transition-colors",
                              isFull && "opacity-40 cursor-default",
                              !isFull && "hover:bg-muted/50"
                            )}
                            onClick={() => {
                              if (!isFull) {
                                setSelectedTime(slot.time);
                                setSelectedCourts([]);
                              }
                            }}
                            disabled={isFull}
                          >
                            <span className="font-semibold tabular-nums">{slot.time}</span>
                            <span className={cn("text-muted-foreground", isFull && "text-destructive")}>
                              {isFull
                                ? t('full')
                                : `${slot.availableCourts} ${slot.availableCourts === 1 ? t('court') : t('courts')}`}
                            </span>
                            <span
                              className={cn(
                                "font-medium",
                                slot.isPeak ? "text-amber-600" : "text-muted-foreground"
                              )}
                            >
                              {slot.perHour != null ? `${slot.perHour} kr/h` : ""}
                            </span>
                          </button>
                          {isFull && (
                            <WaitlistCta
                              compact
                              courts={courts}
                              date={selectedDate}
                              startTime={slot.time}
                              durationMinutes={duration}
                              className="mr-2 shrink-0"
                            />
                          )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Court selection (shown when time is selected) */}
      {selectedTime && (
        <div className="space-y-4 animate-in fade-in-0 slide-in-from-right-4 duration-200">
          {/* Compact time header with back button */}
          <div className="flex items-center justify-between rounded-xl border px-4 py-3">
            <button
              onClick={() => {
                setSelectedTime(null);
                setSelectedCourts([]);
              }}
              className="flex items-center gap-1.5 text-sm text-primary font-medium hover:underline"
            >
              <ChevronLeft className="h-4 w-4" />
              {t('changeTime')}
            </button>
            <div className="text-right">
              <p className="text-sm font-semibold">{selectedTime}</p>
              <p className="text-[11px] text-muted-foreground">
                {format(selectedDate, "EEEE d MMMM", { locale: sv })}
              </p>
            </div>
          </div>

          {/* Duration selector */}
          {durationOptions.length > 1 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {t('playTime')}
              </p>
              <div className="flex gap-2 flex-wrap">
                {durationOptions.map((d) => (
                  <button
                    key={d}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium border transition-colors",
                      duration === d
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground border-border hover:bg-muted",
                    )}
                    onClick={() => handleDurationChange(d)}
                  >
                    {t(`duration.min${d}` as const)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Court list */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {t('selectCourt')}
            </p>
            <div className="rounded-xl border divide-y">
              {courts.map((court) => {
                const isFree = isCourtFreeForDuration(court.id, selectedTime, duration);
                const isSelected = selectedCourts.includes(court.id);
                const price = isFree ? getCourtPrice(court.id, selectedTime, duration) : 0;

                if (!isFree) {
                  return (
                    <div
                      key={court.id}
                      className="flex items-center gap-3 px-4 py-3 opacity-40"
                    >
                      <div className="h-5 w-5 rounded border border-muted-foreground/30 flex items-center justify-center shrink-0">
                        <Square className="h-3.5 w-3.5 text-muted-foreground/30" />
                      </div>
                      <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <SportIcon size="sm" className="text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{court.name}</p>
                      </div>
                      <Badge variant="secondary" className="ml-auto shrink-0 opacity-60">{t('booked')}</Badge>
                    </div>
                  );
                }

                return (
                  <button
                    key={court.id}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 transition-colors text-left",
                      isSelected
                        ? "bg-primary/5 border-l-2 border-l-primary"
                        : "hover:bg-muted/50",
                    )}
                    onClick={() => toggleCourt(court.id)}
                  >
                    <div className={cn(
                      "h-5 w-5 rounded border flex items-center justify-center shrink-0 transition-colors",
                      isSelected
                        ? "bg-primary border-primary"
                        : "border-muted-foreground/40",
                    )}>
                      {isSelected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                    </div>
                    <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <SportIcon size="sm" className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{court.name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {court.surface_type ?? (court.court_type === "single" ? t('single') : t('double'))}{court.max_players ? ` · ${court.max_players} ${t('players')}` : ""}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-muted-foreground ml-auto shrink-0">
                      {price > 0 ? `${price} kr` : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Price hint */}
          {selectedCourts.length === 0 && (() => {
            const slot = allSlots.find(s => s.time === selectedTime);
            if (!slot?.perHour) return null;
            return (
              <p className="text-xs text-muted-foreground text-center">
                {slot.isPeak
                  ? t('priceFromPeak', { price: slot.perHour })
                  : t('priceFrom', { price: slot.perHour })}
              </p>
            );
          })()}

          {/* Sticky footer when courts selected */}
          {selectedCourts.length > 0 && (
            <div className="sticky bottom-0 bg-background border-t pt-3 pb-4 -mx-4 px-4 space-y-3 z-10">
              <p className="text-sm font-medium text-center">
                {t('courtsSelected', { count: selectedCourts.length })} · {totalPrice} kr
              </p>
              <Button
                className="w-full"
                size="lg"
                onClick={handleBookSelected}
              >
                {t('bookNCourts', { count: selectedCourts.length })}
              </Button>
              <Button
                className="w-full"
                variant="outline"
                size="lg"
                onClick={handleCreateActivity}
              >
                {t('createActivityOnCourts', { count: selectedCourts.length })}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
